import type React from "react";
import { useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DisabledButtonTooltip from "./DisabledButtonTooltip";

export interface DetailHeaderAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  disabledHint?: React.ReactNode;
  testId?: string;
  /** Completely omit from UI (role/visibility gated). */
  hidden?: boolean;
}

interface DetailHeaderSplitActionsProps {
  /** Standalone buttons shown beside the split (e.g. enabled Edit). */
  leadingActions?: DetailHeaderAction[];
  primary: DetailHeaderAction;
  menuActions: DetailHeaderAction[];
  menuTestId: string;
  /** When true, hide primary/leading buttons and show only the menu trigger with all actions disabled. */
  finalState?: boolean;
}

const buttonSx = {
  textTransform: "none" as const,
  fontWeight: 600,
  px: 2,
};

const dedupeActionsById = (actions: DetailHeaderAction[]): DetailHeaderAction[] => {
  const seen = new Set<string>();
  return actions.filter((action) => {
    if (seen.has(action.id)) {
      return false;
    }
    seen.add(action.id);
    return true;
  });
};

const DetailHeaderSplitActions: React.FC<DetailHeaderSplitActionsProps> = ({
  leadingActions = [],
  primary,
  menuActions,
  menuTestId,
  finalState = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const visibleLeading = leadingActions.filter((action) => !action.hidden);
  const visibleMenu = menuActions.filter((action) => !action.hidden);
  const menuItems = finalState
    ? dedupeActionsById([...visibleLeading, primary, ...visibleMenu]).map((action) => ({
        ...action,
        disabled: true,
      }))
    : visibleMenu;

  const closeMenu = () => setMenuOpen(false);

  const handleMenuAction = (action: DetailHeaderAction) => {
    if (action.disabled) {
      return;
    }
    closeMenu();
    action.onClick();
  };

  const menuTriggerButton = (
    <Button
      variant="contained"
      color="primary"
      size="small"
      aria-controls={menuOpen ? menuTestId : undefined}
      aria-expanded={menuOpen ? "true" : undefined}
      aria-haspopup="menu"
      onClick={() => setMenuOpen((open) => !open)}
      data-testid={menuTestId}
      sx={{
        ...buttonSx,
        minWidth: 0,
        px: 0.75,
        borderRadius: 1.5,
      }}
    >
      <ArrowDropDownIcon />
    </Button>
  );

  const splitButton = (
    <ButtonGroup
      variant="contained"
      color="primary"
      ref={anchorRef}
      sx={{
        borderRadius: 1.5,
        "& .MuiButtonGroup-grouped": {
          minWidth: 0,
        },
      }}
    >
      <Button
        disabled={primary.disabled}
        startIcon={primary.icon}
        onClick={primary.onClick}
        data-testid={primary.testId}
        sx={{
          ...buttonSx,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        }}
      >
        {primary.label}
      </Button>
      <Button
        size="small"
        aria-controls={menuOpen ? menuTestId : undefined}
        aria-expanded={menuOpen ? "true" : undefined}
        aria-haspopup="menu"
        onClick={() => setMenuOpen((open) => !open)}
        data-testid={menuTestId}
        sx={{
          px: 0.75,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        }}
      >
        <ArrowDropDownIcon />
      </Button>
    </ButtonGroup>
  );

  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
      {!finalState &&
        visibleLeading.map((action) => (
          <DisabledButtonTooltip
            key={action.id}
            title={action.disabledHint ?? ""}
            disabled={!!action.disabled}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={action.disabled}
              startIcon={action.icon}
              onClick={action.onClick}
              data-testid={action.testId}
              sx={{ ...buttonSx, borderRadius: 1.5 }}
            >
              {action.label}
            </Button>
          </DisabledButtonTooltip>
        ))}

      <Box ref={anchorRef}>
        {finalState ? (
          menuTriggerButton
        ) : (
          <DisabledButtonTooltip title={primary.disabledHint ?? ""} disabled={!!primary.disabled}>
            {splitButton}
          </DisabledButtonTooltip>
        )}
      </Box>

      <Menu
        id={menuTestId}
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menuItems.map((action) => {
          if (!action.disabled || !action.disabledHint) {
            return (
              <MenuItem
                key={action.id}
                disabled={action.disabled}
                onClick={() => handleMenuAction(action)}
                data-testid={action.testId}
              >
                {action.icon && (
                  <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>{action.icon}</ListItemIcon>
                )}
                <ListItemText>{action.label}</ListItemText>
              </MenuItem>
            );
          }

          return (
            <Tooltip key={action.id} title={action.disabledHint} arrow placement="left">
              <Box component="span" sx={{ display: "block" }}>
                <MenuItem disabled data-testid={action.testId}>
                  {action.icon && (
                    <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
                      {action.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText>{action.label}</ListItemText>
                </MenuItem>
              </Box>
            </Tooltip>
          );
        })}
      </Menu>
    </Box>
  );
};

export default DetailHeaderSplitActions;
